import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PaymentService } from '../payment.service';
import { Settlement, SettlementProof } from '../payment.type';
import { ScreenshotPreview } from '../../account/screenshot-preview/screenshot-preview';
import { ConfirmDialog } from '../../shared/confirm-dialog/confirm-dialog';
import { MAX_PROOFS, PROOF_ACCEPT, addProofFiles } from '../proof-files';

export interface SettlementProofsDialogData {
  settlement: Settlement;
  readOnly?: boolean;
}

interface LoadedProof extends SettlementProof {
  blob?: Blob;
  url?: string;
  error?: boolean;
}

/**
 * Every payment proof of one settlement: view each, attach more, remove one
 * attached by mistake. Works whatever the settlement's status — a proof often
 * turns up after the payment is marked. Closes with true if anything changed,
 * so the list can refresh its proof count.
 */
@Component({
  selector: 'app-settlement-proofs-dialog',
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
  templateUrl: './settlement-proofs-dialog.html',
  styleUrl: './settlement-proofs-dialog.scss',
})
export class SettlementProofsDialog implements OnInit, OnDestroy {
  private paymentService = inject(PaymentService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  readonly dialogRef = inject<MatDialogRef<SettlementProofsDialog, boolean>>(MatDialogRef);
  readonly data = inject<SettlementProofsDialogData>(MAT_DIALOG_DATA);
  readonly accept = PROOF_ACCEPT;
  readonly maxProofs = MAX_PROOFS;

  proofs: LoadedProof[] = [];
  loading = true;
  busy = false;
  error: string | null = null;
  fileError = '';
  private changed = false;

  ngOnInit(): void {
    this.load();
  }

  get room(): number {
    return Math.max(0, MAX_PROOFS - this.proofs.length);
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.paymentService.listSettlementProofs(this.data.settlement.id).subscribe({
      next: list => {
        this.revokeAll();
        this.proofs = list.map(p => ({ ...p }));
        this.loading = false;
        for (const p of this.proofs) this.fetchImage(p);
      },
      error: err => { this.error = err?.error?.error ?? 'Failed to load proofs'; this.loading = false; },
    });
  }

  private fetchImage(p: LoadedProof): void {
    this.paymentService.getSettlementProofImage(this.data.settlement.id, p.id).subscribe({
      next: blob => { p.blob = blob; p.url = URL.createObjectURL(blob); },
      error: () => { p.error = true; },
    });
  }

  view(p: LoadedProof): void {
    if (!p.blob || !p.url) return;
    this.dialog.open(ScreenshotPreview, {
      data: { imageUrl: p.url, imageBlob: p.blob },
      maxWidth: '95vw',
      maxHeight: '95vh',
      panelClass: 'screenshot-dialog',
    });
  }

  onFilesSelected(event: Event): void {
    const { files, error } = addProofFiles(event.target as HTMLInputElement, [], this.room);
    this.fileError = error;
    if (!files.length) return;
    this.busy = true;
    this.paymentService.addSettlementProofs(this.data.settlement.id, files).subscribe({
      next: added => {
        this.busy = false;
        this.changed = true;
        this.snackBar.open(`${added.length} proof${added.length === 1 ? '' : 's'} added`, 'Close', { duration: 3000 });
        this.load();
      },
      error: err => {
        this.busy = false;
        this.snackBar.open(err?.error?.error ?? 'Failed to add proofs', 'Close', { duration: 6000 });
      },
    });
  }

  remove(p: LoadedProof): void {
    this.dialog.open(ConfirmDialog, {
      data: { title: 'Remove proof', message: 'Remove this payment proof from the settlement? This cannot be undone.' },
    }).afterClosed().subscribe(ok => {
      if (!ok) return;
      this.busy = true;
      this.paymentService.deleteSettlementProof(this.data.settlement.id, p.id).subscribe({
        next: () => {
          this.busy = false;
          this.changed = true;
          if (p.url) URL.revokeObjectURL(p.url);
          this.proofs = this.proofs.filter(x => x.id !== p.id);
          this.fileError = '';
        },
        error: err => {
          this.busy = false;
          this.snackBar.open(err?.error?.error ?? 'Failed to remove proof', 'Close', { duration: 6000 });
        },
      });
    });
  }

  close(): void {
    this.dialogRef.close(this.changed);
  }

  private revokeAll(): void {
    for (const p of this.proofs) if (p.url) URL.revokeObjectURL(p.url);
  }

  ngOnDestroy(): void {
    this.revokeAll();
  }
}
